/**
 * `DeliveryNoteModal` — modal complet de création + signature d'un bon
 * de remise de matériel (iter110n · CEO 2026-09-27).
 *
 * Flow en 3 étapes sur le MÊME appareil (remise sur le terrain avec 1
 * seule tablette/téléphone) :
 *
 *   1. Composer  → Sélection type + articles du catalogue + « Autre »
 *   2. Signer 1  → Le salarié signe (recipient)
 *   3. Signer 2  → La personne remettante signe (deliverer)
 *
 * Une fois les 2 signatures capturées, le bon est verrouillé (SIGNED)
 * et un PDF est téléchargeable via `/api/equipment/notes/{id}/pdf`.
 *
 * Props :
 *   - open, onOpenChange
 *   - subjectType         : "candidate" | "permanent"
 *   - subjectId           : id du destinataire
 *   - subjectName         : nom d'affichage
 *   - defaultType         : "initial" (default) | "complementary" | "return" | "regularization"
 *   - preselectedItems    : [{catalog_id, quantity, ...}] pour une restitution pré-remplie
 *   - onCompleted(note)   : callback après signature finale
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { CheckCircle2, ClipboardCheck, PenSquare, FileDown, ArrowRight, ArrowLeft, Minus, Plus as PlusIcon, Pencil, X as XIcon, Mail, Smartphone, UserCheck, Send } from "lucide-react";
import { api } from "../lib/api";
import { openAuthedPdf } from "../lib/pdf";
import SignaturePad from "./signature/SignaturePad";
import { SignatureModeSwitch, TypedSignaturePreview, encodeTypedSig } from "./signature/TypedSignature";

const TYPES = [
    { id: "initial", label: "Remise initiale", desc: "1ère remise au démarrage" },
    { id: "complementary", label: "Remise complémentaire", desc: "Ajout après une remise initiale" },
    { id: "return", label: "Restitution", desc: "Le salarié restitue le matériel" },
    { id: "regularization", label: "Régularisation", desc: "Salarié déjà en poste, remise antérieure à régulariser" },
];

export default function DeliveryNoteModal({
    open, onOpenChange,
    subjectType, subjectId, subjectName,
    defaultType = "initial",
    preselectedItems = null,
    onCompleted,
}) {
    // Step machine
    const [step, setStep] = useState("compose"); // compose | sign_recipient | sign_deliverer | done
    const [type, setType] = useState(defaultType);
    const [catalog, setCatalog] = useState([]);
    const [selectedItems, setSelectedItems] = useState({});  // { catalog_id: {quantity, size, serial_number} }
    const [customItems, setCustomItems] = useState([]);      // [{name, quantity, unit_value_eur}]
    const [notes, setNotes] = useState("");
    const [heldEquipment, setHeldEquipment] = useState(null);
    const [note, setNote] = useState(null);
    const [recipientSig, setRecipientSig] = useState(null);
    const [recipientName, setRecipientName] = useState(subjectName || "");
    const [recipientTyped, setRecipientTyped] = useState(true);  // CEO 2026-10-07 · typed par défaut
    const [delivererSig, setDelivererSig] = useState(null);
    const [delivererName, setDelivererName] = useState("");
    const [delivererTyped, setDelivererTyped] = useState(true);  // CEO 2026-10-07 · typed par défaut
    const [saving, setSaving] = useState(false);
    const [signing, setSigning] = useState(false);
    // iter110n-2 · Ligne actuellement en mode « pencil » (override pour ce bon uniquement)
    const [editingLineId, setEditingLineId] = useState(null);
    // CEO 2026-10-07 · Mode de signature du salarié : onsite | email | app
    const [mode, setMode] = useState("onsite");
    const [channels, setChannels] = useState(null);

    // Load catalog + held equipment on open
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const [{ data: catData }, { data: heldData }] = await Promise.all([
                    api.get("/equipment/catalog"),
                    api.get(`/equipment/held/${subjectType}/${subjectId}`),
                ]);
                if (cancelled) return;
                setCatalog(catData.items || []);
                setHeldEquipment(heldData);
                // For "return" type, pre-fill with what the person holds
                if (defaultType === "return" && heldData.items?.length) {
                    const preset = {};
                    for (const it of heldData.items) {
                        if (it.catalog_id) {
                            preset[it.catalog_id] = {
                                quantity: it.quantity_held,
                                size: it.size || "",
                                serial_number: it.serial_number || "",
                            };
                        }
                    }
                    setSelectedItems(preset);
                }
            } catch (err) {
                toast.error("Impossible de charger le catalogue");
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, subjectType, subjectId, defaultType]);

    // Reset when modal closes
    useEffect(() => {
        if (open) return;
        setStep("compose");
        setType(defaultType);
        setSelectedItems({});
        setCustomItems([]);
        setNotes("");
        setNote(null);
        setRecipientSig(null);
        setDelivererSig(null);
        setDelivererName("");
        setRecipientName(subjectName || "");
        setEditingLineId(null);
        setMode("onsite");
        setChannels(null);
    }, [open, defaultType, subjectName]);

    // iter110n-2 · Retourne la valeur unitaire effective pour cette ligne
    // (override_value du bon si défini, sinon prix catalogue).
    const effectiveUnitValue = (cat, meta) => {
        if (meta?.override_value != null && meta.override_value !== "") {
            const v = parseFloat(meta.override_value);
            if (!Number.isNaN(v)) return v;
        }
        return cat?.unit_value_eur ?? 0;
    };

    const totalEur = useMemo(() => {
        let t = 0;
        for (const [cid, meta] of Object.entries(selectedItems)) {
            const cat = catalog.find((c) => c.id === cid);
            if (!cat || !meta.quantity || meta.quantity <= 0) continue;
            // Article en usage partagé → n'entre pas dans la valeur
            if (meta.usage_mode === "shared") continue;
            t += effectiveUnitValue(cat, meta) * meta.quantity;
        }
        for (const ci of customItems) {
            t += (ci.unit_value_eur || 0) * (ci.quantity || 0);
        }
        return Math.round(t * 100) / 100;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItems, customItems, catalog]);

    const itemCount = useMemo(() => (
        Object.values(selectedItems).filter((m) => m.quantity > 0).length + customItems.length
    ), [selectedItems, customItems]);

    const toggleItem = (item) => {
        setSelectedItems((prev) => {
            const next = { ...prev };
            if (next[item.id]) {
                delete next[item.id];
            } else {
                next[item.id] = {
                    quantity: 1,
                    size: item.sizes?.[0] || "",
                    serial_number: "",
                    // iter110n-2 · Défauts pour variante et mode d'usage
                    variant: item.variant_kind === "association" ? (item.variant_options?.[0] || "") : "",
                    usage_mode: item.allow_shared_use ? "personal" : null,
                    override_name: null,
                    override_value: null,
                };
            }
            return next;
        });
    };

    const updateItem = (catalogId, field, value) => {
        setSelectedItems((prev) => ({
            ...prev,
            [catalogId]: { ...prev[catalogId], [field]: value },
        }));
    };

    const incrementQty = (catalogId, delta) => {
        setSelectedItems((prev) => {
            const meta = prev[catalogId];
            if (!meta) return prev;
            const next = Math.max(1, (parseInt(meta.quantity, 10) || 1) + delta);
            return { ...prev, [catalogId]: { ...meta, quantity: next } };
        });
    };

    const addCustomItem = () => {
        setCustomItems((prev) => [...prev, { name: "", quantity: 1, unit_value_eur: 0, size: "", serial_number: "" }]);
    };
    const updateCustomItem = (i, field, value) => {
        setCustomItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));
    };
    const removeCustomItem = (i) => {
        setCustomItems((prev) => prev.filter((_, idx) => idx !== i));
    };

    const buildItemsPayload = () => {
        const items = [];
        for (const [cid, meta] of Object.entries(selectedItems)) {
            if (!meta.quantity || meta.quantity <= 0) continue;
            const cat = catalog.find((c) => c.id === cid);
            if (!cat) continue;
            const name = (meta.override_name && meta.override_name.trim()) || cat.name;
            items.push({
                catalog_id: cid,
                name,
                quantity: parseInt(meta.quantity, 10),
                size: meta.size || null,
                serial_number: meta.serial_number || null,
                unit_value_eur: effectiveUnitValue(cat, meta),
                variant: meta.variant || null,
                usage_mode: meta.usage_mode || null,
            });
        }
        for (const ci of customItems) {
            if (!ci.name || !ci.quantity) continue;
            items.push({
                catalog_id: null,
                name: ci.name,
                quantity: parseInt(ci.quantity, 10),
                size: ci.size || null,
                serial_number: ci.serial_number || null,
                unit_value_eur: parseFloat(ci.unit_value_eur) || 0,
            });
        }
        return items;
    };

    const handleCreateNote = async () => {
        const items = buildItemsPayload();
        if (!items.length) {
            toast.error("Ajoute au moins un article");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/equipment/notes", {
                subject_type: subjectType,
                subject_id: subjectId,
                type, items, notes: notes || null,
            });
            setNote(data);
            setStep("choose_mode");
            api.get(`/equipment/notes/${data.id}/recipient-channels`)
                .then(({ data: ch }) => setChannels(ch))
                .catch(() => setChannels({ email: null, app_user: null }));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur à la création du bon");
        } finally {
            setSaving(false);
        }
    };

    const handleSignRecipient = async () => {
        const sigPayload = recipientTyped ? encodeTypedSig(recipientName) : recipientSig;
        if (recipientTyped && !recipientName.trim()) {
            toast.error("Entrez votre nom pour la signature");
            return;
        }
        if (!recipientTyped && !recipientSig) { toast.error("Signature du salarié requise"); return; }
        setSigning(true);
        try {
            const { data } = await api.post(`/equipment/notes/${note.id}/sign`, {
                role: "recipient",
                signature_data_uri: sigPayload,
                full_name: recipientName || subjectName,
                typed: recipientTyped,
            });
            setNote(data);
            setStep("sign_deliverer");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur à la signature");
        } finally {
            setSigning(false);
        }
    };

    const handleSignDeliverer = async () => {
        const sigPayload = delivererTyped ? encodeTypedSig(delivererName) : delivererSig;
        if (!delivererName.trim()) { toast.error("Nom du remettant requis"); return; }
        if (!delivererTyped && !delivererSig) { toast.error("Signature du remettant requise"); return; }
        setSigning(true);
        try {
            const { data } = await api.post(`/equipment/notes/${note.id}/sign`, {
                role: "deliverer",
                signature_data_uri: sigPayload,
                full_name: delivererName,
                typed: delivererTyped,
            });
            if (mode !== "onsite") {
                const { data: sent } = await api.post(`/equipment/notes/${note.id}/request-signature`, { channel: mode });
                setNote(sent);
                setStep("sent");
                toast.success(mode === "email" ? "Lien de signature envoyé par email" : "Bon envoyé dans son espace Prérec");
                onCompleted?.(sent);
                return;
            }
            setNote(data);
            setStep("done");
            toast.success("Bon signé et archivé · copie PDF envoyée aux 2 parties");
            onCompleted?.(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur à la signature");
        } finally {
            setSigning(false);
        }
    };

    // iter110n-3 fix · Fetch PDF with auth header, then open blob URL
    // (window.open direct URL fails on protected endpoints — no auth
    // header attached, backend returns 401 "Token manquant").
    const downloadPdf = () => {
        if (!note?.id) return;
        return openAuthedPdf(`/equipment/notes/${note.id}/pdf`);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[92vh] overflow-y-auto p-0"
                data-testid="delivery-note-modal"
            >
                {/* Header */}
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white px-6 py-5">
                    <DialogHeader className="space-y-1 text-left">
                        <DialogTitle className="text-2xl font-black flex items-center gap-2">
                            <ClipboardCheck className="w-6 h-6" />
                            Bon de remise de matériel
                        </DialogTitle>
                        <DialogDescription className="text-orange-50">
                            À <strong className="text-white">{subjectName}</strong>
                            {heldEquipment?.items?.length ? (
                                <span className="ml-2 text-[13px] opacity-90">
                                    · détient actuellement {heldEquipment.items.length} article{heldEquipment.items.length > 1 ? "s" : ""} ({heldEquipment.total_value_eur} €)
                                </span>
                            ) : null}
                        </DialogDescription>
                    </DialogHeader>
                </div>

                {/* Stepper */}
                <div className="px-6 pt-4 pb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider">
                    <StepPill active={step === "compose"} done={step !== "compose"}>1 · Composer</StepPill>
                    <span className="text-slate-300">→</span>
                    <StepPill active={step === "choose_mode"} done={!["compose", "choose_mode"].includes(step)}>2 · Mode de signature</StepPill>
                    <span className="text-slate-300">→</span>
                    <StepPill active={["sign_recipient", "sign_deliverer"].includes(step)} done={["done", "sent"].includes(step)}>
                        3 · Signatures
                    </StepPill>
                </div>

                {/* ============ STEP 1 : COMPOSE ============ */}
                {step === "compose" && (
                    <div className="px-6 py-4 space-y-5">
                        {/* Type selection */}
                        <div>
                            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                                Type de bon
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {TYPES.map((t) => (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => setType(t.id)}
                                        data-testid={`delivery-note-type-${t.id}`}
                                        className={`rounded-lg border-2 p-3 text-left transition ${
                                            type === t.id
                                                ? "border-orange-500 bg-orange-50 shadow-sm"
                                                : "border-slate-200 hover:border-orange-300 hover:bg-orange-50/40"
                                        }`}
                                    >
                                        <div className="text-sm font-bold">{t.label}</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">{t.desc}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Catalog */}
                        <div>
                            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                                Catalogue matériel · coche ce qui est remis
                            </div>
                            <div className="border border-slate-200 rounded-lg divide-y max-h-80 overflow-y-auto">
                                {catalog.length === 0 && (
                                    <div className="p-4 text-sm text-slate-500 italic text-center">
                                        Catalogue vide — ajoute des articles depuis l'onglet Matériel &gt; Catalogue.
                                    </div>
                                )}
                                {catalog.map((item) => {
                                    const isSelected = !!selectedItems[item.id];
                                    const meta = selectedItems[item.id] || {};
                                    const isEditing = editingLineId === item.id;
                                    const effectiveValue = effectiveUnitValue(item, meta);
                                    const displayName = (meta.override_name && meta.override_name.trim()) || item.name;
                                    const isShared = meta.usage_mode === "shared";
                                    return (
                                        <div
                                            key={item.id}
                                            className={`px-3 py-3 ${isSelected ? "bg-orange-50/60" : ""}`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleItem(item)}
                                                    data-testid={`delivery-note-catalog-${item.id}`}
                                                    className="mt-1.5 w-5 h-5 accent-orange-500 cursor-pointer shrink-0"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <div className="font-semibold text-sm truncate">{displayName}</div>
                                                            {item.category && (
                                                                <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded shrink-0">
                                                                    {item.category}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {isSelected && (
                                                            <div className="flex items-center gap-2">
                                                                {/* Qty ± buttons */}
                                                                <div className="inline-flex items-center rounded-md border border-slate-300 bg-white overflow-hidden">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => incrementQty(item.id, -1)}
                                                                        disabled={meta.quantity <= 1}
                                                                        className="px-2 py-1 hover:bg-slate-100 disabled:opacity-40"
                                                                        data-testid={`delivery-note-qty-dec-${item.id}`}
                                                                        aria-label="Diminuer la quantité"
                                                                    >
                                                                        <Minus className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    <div
                                                                        className="px-3 text-sm font-mono font-bold tabular-nums min-w-[2rem] text-center"
                                                                        data-testid={`delivery-note-qty-${item.id}`}
                                                                    >
                                                                        {meta.quantity}
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => incrementQty(item.id, +1)}
                                                                        className="px-2 py-1 hover:bg-slate-100"
                                                                        data-testid={`delivery-note-qty-inc-${item.id}`}
                                                                        aria-label="Augmenter la quantité"
                                                                    >
                                                                        <PlusIcon className="w-3.5 h-3.5" />
                                                                    </button>
                                                                </div>
                                                                {/* Live line total */}
                                                                <div className={`text-sm font-mono font-bold tabular-nums ${isShared ? "text-slate-400 line-through" : "text-slate-800"}`}>
                                                                    {(effectiveValue * (meta.quantity || 1)).toFixed(2)} €
                                                                </div>
                                                                {/* Pencil to edit this line only */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEditingLineId(isEditing ? null : item.id)}
                                                                    className={`p-1.5 rounded hover:bg-orange-100 transition ${isEditing ? "bg-orange-100 text-orange-700" : "text-slate-500"}`}
                                                                    data-testid={`delivery-note-edit-${item.id}`}
                                                                    title="Modifier cette ligne (ce bon uniquement)"
                                                                >
                                                                    <Pencil className="w-3.5 h-3.5" />
                                                                </button>
                                                            </div>
                                                        )}
                                                        {!isSelected && (
                                                            <div className="text-xs text-slate-500 shrink-0 font-mono">
                                                                {item.unit_value_eur.toFixed(2)} €
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Variant dropdown (association) + Usage mode (shared) */}
                                                    {isSelected && (
                                                        <div className="mt-2 flex flex-wrap gap-2 text-sm">
                                                            {item.variant_kind === "association" && (
                                                                <select
                                                                    value={meta.variant || ""}
                                                                    onChange={(e) => updateItem(item.id, "variant", e.target.value)}
                                                                    className="h-8 px-2 rounded-md border border-slate-300 bg-white text-sm"
                                                                    data-testid={`delivery-note-variant-${item.id}`}
                                                                >
                                                                    {(item.variant_options || []).map((opt) => (
                                                                        <option key={opt} value={opt}>{opt}</option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                            {item.needs_size && (
                                                                <select
                                                                    value={meta.size || ""}
                                                                    onChange={(e) => updateItem(item.id, "size", e.target.value)}
                                                                    className="h-8 px-2 rounded-md border border-slate-300 bg-white text-sm"
                                                                    data-testid={`delivery-note-size-${item.id}`}
                                                                >
                                                                    {(item.sizes || []).map((s) => (
                                                                        <option key={s} value={s}>Taille {s}</option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                            {item.allow_shared_use && (
                                                                <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => updateItem(item.id, "usage_mode", "personal")}
                                                                        className={`px-3 py-1.5 font-semibold ${meta.usage_mode === "personal" ? "bg-orange-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                                                                        data-testid={`delivery-note-usage-personal-${item.id}`}
                                                                    >
                                                                        Remise personnelle
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => updateItem(item.id, "usage_mode", "shared")}
                                                                        className={`px-3 py-1.5 font-semibold ${meta.usage_mode === "shared" ? "bg-slate-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                                                                        data-testid={`delivery-note-usage-shared-${item.id}`}
                                                                    >
                                                                        Usage partagé
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Explanatory hint for shared use */}
                                                    {isSelected && isShared && (
                                                        <div className="mt-2 text-[11px] text-slate-500 italic bg-slate-100 rounded px-2 py-1">
                                                            Inventaire du responsable d'équipe · prêtée au salarié sur le terrain, restituée en fin de journée. Non comptabilisée dans la valeur du salarié.
                                                        </div>
                                                    )}

                                                    {/* Serial number capture (only when needed) */}
                                                    {isSelected && item.needs_serial && (
                                                        <div className="mt-2">
                                                            <Input
                                                                value={meta.serial_number || ""}
                                                                onChange={(e) => updateItem(item.id, "serial_number", e.target.value)}
                                                                placeholder="N° de série"
                                                                className="text-sm h-8"
                                                                data-testid={`delivery-note-serial-${item.id}`}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Pencil-opened override editor — this bon only */}
                                                    {isSelected && isEditing && (
                                                        <div className="mt-3 p-3 rounded-md border-2 border-dashed border-orange-300 bg-white space-y-2">
                                                            <div className="text-[11px] font-bold text-orange-700 uppercase tracking-wider">
                                                                Modifier pour ce bon uniquement
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                <div>
                                                                    <label className="text-[10px] uppercase text-slate-500 font-semibold">Intitulé override</label>
                                                                    <Input
                                                                        value={meta.override_name || ""}
                                                                        onChange={(e) => updateItem(item.id, "override_name", e.target.value)}
                                                                        placeholder={item.name}
                                                                        className="text-sm h-8"
                                                                        data-testid={`delivery-note-override-name-${item.id}`}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="text-[10px] uppercase text-slate-500 font-semibold">Valeur unitaire (€)</label>
                                                                    <Input
                                                                        type="number" step="0.01" min="0"
                                                                        value={meta.override_value ?? ""}
                                                                        onChange={(e) => updateItem(item.id, "override_value", e.target.value)}
                                                                        placeholder={item.unit_value_eur.toFixed(2)}
                                                                        className="text-sm h-8"
                                                                        data-testid={`delivery-note-override-value-${item.id}`}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="flex justify-end gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        updateItem(item.id, "override_name", null);
                                                                        updateItem(item.id, "override_value", null);
                                                                    }}
                                                                    className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 uppercase tracking-wider"
                                                                >
                                                                    Rétablir
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setEditingLineId(null)}
                                                                    className="text-[11px] font-semibold text-orange-600 hover:text-orange-800 uppercase tracking-wider inline-flex items-center gap-1"
                                                                >
                                                                    <XIcon className="w-3 h-3" /> Fermer
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom items */}
                        <div>
                            <div className="flex items-baseline justify-between mb-2">
                                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                                    Autres articles (hors catalogue)
                                </div>
                                <button
                                    type="button"
                                    onClick={addCustomItem}
                                    className="text-xs font-semibold text-orange-600 hover:text-orange-700"
                                    data-testid="delivery-note-add-custom"
                                >
                                    + Ajouter
                                </button>
                            </div>
                            {customItems.map((ci, i) => (
                                <div key={i} className="mb-2 grid grid-cols-12 gap-2">
                                    <Input
                                        value={ci.name} placeholder="Désignation"
                                        onChange={(e) => updateCustomItem(i, "name", e.target.value)}
                                        className="col-span-5 text-sm h-8"
                                    />
                                    <Input
                                        type="number" min={1} value={ci.quantity} placeholder="Qté"
                                        onChange={(e) => updateCustomItem(i, "quantity", parseInt(e.target.value, 10))}
                                        className="col-span-2 text-sm h-8"
                                    />
                                    <Input
                                        type="number" step="0.01" min={0} value={ci.unit_value_eur} placeholder="Val. € u."
                                        onChange={(e) => updateCustomItem(i, "unit_value_eur", parseFloat(e.target.value))}
                                        className="col-span-3 text-sm h-8"
                                    />
                                    <button
                                        type="button" onClick={() => removeCustomItem(i)}
                                        className="col-span-2 text-xs text-red-600 hover:underline"
                                    >
                                        Retirer
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Notes */}
                        <div>
                            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                                Observations (optionnel)
                            </div>
                            <Textarea
                                value={notes} onChange={(e) => setNotes(e.target.value)}
                                rows={2}
                                placeholder="Ex : matériel remis en présence du référent, état neuf..."
                                className="text-sm"
                            />
                        </div>

                        {/* Total */}
                        <div className="flex items-center justify-between p-3 bg-slate-900 text-white rounded-lg">
                            <div className="text-sm font-semibold">{itemCount} article{itemCount > 1 ? "s" : ""}</div>
                            <div className="text-2xl font-black text-orange-400">{totalEur.toFixed(2)} €</div>
                        </div>
                    </div>
                )}

                {step === "choose_mode" && (
                    <SignModeChooser channels={channels} subjectName={subjectName} mode={mode} setMode={setMode} />
                )}

                {step === "sent" && (
                    <div className="px-6 py-8 text-center space-y-3" data-testid="delivery-note-sent">
                        <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                            <Send className="w-8 h-8 text-amber-600" />
                        </div>
                        <div className="text-xl font-black text-slate-800">En attente de la signature du salarié</div>
                        <div className="text-sm text-slate-600">
                            {mode === "email"
                                ? <>Lien envoyé à <strong>{note?.remote_sign?.email}</strong>.</>
                                : <>Le bon est disponible dans l'espace Prérec de <strong>{note?.remote_sign?.app_user_name}</strong>.</>}
                            {" "}Dès sa signature, chacun recevra le PDF signé par email.
                        </div>
                    </div>
                )}

                {/* ============ STEP 2 : RECIPIENT SIGNATURE ============ */}
                {step === "sign_recipient" && (
                    <div className="px-6 py-4 space-y-4">
                        <div className="text-center py-2">
                            <div className="text-lg font-black text-slate-800">
                                Signature du salarié
                            </div>
                            <div className="text-sm text-slate-600 mt-1">
                                <strong>{subjectName}</strong>, signe ci-dessous pour confirmer la réception du matériel.
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
                                Nom du signataire
                            </div>
                            <Input
                                value={recipientName}
                                onChange={(e) => setRecipientName(e.target.value)}
                                className="text-sm"
                                data-testid="delivery-note-recipient-name"
                            />
                        </div>
                        <SignatureModeSwitch typed={recipientTyped} setTyped={setRecipientTyped} testid="recipient-sig-mode" />
                        {recipientTyped ? (
                            <TypedSignaturePreview name={recipientName || subjectName || ""} testid="delivery-note-recipient-typed" />
                        ) : (
                            <SignaturePad
                                onChange={(dataUrl, empty) => setRecipientSig(empty ? null : dataUrl)}
                                testIdPrefix="delivery-note-recipient-sig"
                            />
                        )}
                        <div className="text-[11px] text-slate-500 italic text-center">
                            En signant, {recipientName || "le salarié"} reconnaît avoir reçu / restitué le matériel listé.
                        </div>
                    </div>
                )}

                {/* ============ STEP 3 : DELIVERER SIGNATURE ============ */}
                {step === "sign_deliverer" && (
                    <div className="px-6 py-4 space-y-4">
                        <div className="text-center py-2">
                            <div className="text-lg font-black text-slate-800">
                                Signature de la personne remettante
                            </div>
                            <div className="text-sm text-slate-600 mt-1">
                                Le représentant SHARITI (référent, coordinateur, RH…) signe pour valider la remise.
                            </div>
                        </div>
                        <div>
                            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">
                                Nom du remettant
                            </div>
                            <Input
                                value={delivererName}
                                onChange={(e) => setDelivererName(e.target.value)}
                                placeholder="Prénom Nom · fonction (ex: Ben, Coordinateur)"
                                className="text-sm"
                                data-testid="delivery-note-deliverer-name"
                            />
                        </div>
                        <SignatureModeSwitch typed={delivererTyped} setTyped={setDelivererTyped} testid="deliverer-sig-mode" />
                        {delivererTyped ? (
                            <TypedSignaturePreview name={delivererName} testid="delivery-note-deliverer-typed" />
                        ) : (
                            <SignaturePad
                                onChange={(dataUrl, empty) => setDelivererSig(empty ? null : dataUrl)}
                                testIdPrefix="delivery-note-deliverer-sig"
                            />
                        )}
                    </div>
                )}

                {/* ============ STEP 4 : DONE ============ */}
                {step === "done" && (
                    <div className="px-6 py-8 text-center space-y-4">
                        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                        </div>
                        <div>
                            <div className="text-xl font-black text-slate-800">Bon signé et archivé</div>
                            <div className="text-sm text-slate-600 mt-1">
                                Empreinte SHA-256 : <code className="text-[10px] break-all">{note?.signed_document_sha256}</code>
                            </div>
                        </div>
                        <div className="flex justify-center gap-2">
                            <Button variant="outline" onClick={downloadPdf} data-testid="delivery-note-download-pdf">
                                <FileDown className="w-4 h-4 mr-2" /> Télécharger le PDF
                            </Button>
                        </div>
                    </div>
                )}

                {/* Footer buttons */}
                <DialogFooter className="px-6 py-4 border-t border-slate-100 bg-slate-50">
                    {step === "compose" && (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
                            <Button
                                onClick={handleCreateNote}
                                disabled={saving || itemCount === 0}
                                className="bg-orange-500 hover:bg-orange-600 text-white"
                                data-testid="delivery-note-goto-signatures"
                            >
                                Passer aux signatures <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        </>
                    )}
                    {step === "choose_mode" && (
                        <>
                            <Button variant="outline" onClick={() => setStep("compose")}>
                                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                            </Button>
                            <Button
                                onClick={() => setStep(mode === "onsite" ? "sign_recipient" : "sign_deliverer")}
                                className="bg-orange-500 hover:bg-orange-600 text-white"
                                data-testid="delivery-note-mode-continue"
                            >
                                Continuer <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        </>
                    )}
                    {step === "sign_recipient" && (
                        <>
                            <Button variant="outline" onClick={() => setStep("choose_mode")}>
                                <ArrowLeft className="w-4 h-4 mr-1" /> Retour
                            </Button>
                            <Button
                                onClick={handleSignRecipient}
                                disabled={signing || (recipientTyped ? !recipientName.trim() : !recipientSig)}
                                className="bg-orange-500 hover:bg-orange-600 text-white"
                                data-testid="delivery-note-confirm-recipient-sig"
                            >
                                <PenSquare className="w-4 h-4 mr-1" /> Valider la signature
                            </Button>
                        </>
                    )}
                    {step === "sign_deliverer" && (
                        <Button
                            onClick={handleSignDeliverer}
                            disabled={signing || !delivererName.trim() || (!delivererTyped && !delivererSig)}
                            className="bg-orange-500 hover:bg-orange-600 text-white w-full sm:w-auto"
                            data-testid="delivery-note-confirm-deliverer-sig"
                        >
                            <PenSquare className="w-4 h-4 mr-1" />
                            {mode === "onsite" ? "Finaliser et générer le PDF" : mode === "email" ? "Signer et envoyer le lien" : "Signer et envoyer dans son espace"}
                        </Button>
                    )}
                    {["done", "sent"].includes(step) && (
                        <Button
                            data-testid="delivery-note-close"
                            onClick={() => onOpenChange(false)}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white w-full sm:w-auto"
                        >
                            Fermer
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function StepPill({ active, done, children }) {
    return (
        <div className={`px-3 py-1 rounded-full ${
            done ? "bg-emerald-100 text-emerald-700"
                 : active ? "bg-orange-100 text-orange-700"
                 : "bg-slate-100 text-slate-400"
        }`}>
            {children}
        </div>
    );
}


const MODES = [
    { id: "onsite", icon: UserCheck, label: "Sur place", desc: "Le salarié signe sur cet écran, maintenant." },
    { id: "email", icon: Mail, label: "Par email", desc: "Il reçoit un lien pour signer depuis son téléphone." },
    { id: "app", icon: Smartphone, label: "Dans son espace Prérec", desc: "Le bon apparaît dans « Mes bons de matériel »." },
];

function SignModeChooser({ channels, subjectName, mode, setMode }) {
    const disabledReason = (id) => {
        if (id === "email" && channels && !channels.email) return "Aucun email connu";
        if (id === "app" && channels && !channels.app_user) return "Pas d'espace Prérec";
        if (id !== "onsite" && !channels) return "Vérification…";
        return null;
    };
    const detail = (id) => (id === "email" ? channels?.email : id === "app" ? channels?.app_user?.email : null);
    return (
        <div className="px-6 py-4 space-y-3" data-testid="delivery-note-mode-chooser">
            <div className="text-sm text-slate-600">
                Comment <strong>{subjectName}</strong> va-t-il signer ? Dans tous les cas, chacun reçoit le PDF signé par email.
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
                {MODES.map(({ id, icon: Icon, label, desc }) => {
                    const reason = disabledReason(id);
                    return (
                        <button
                            key={id}
                            type="button"
                            disabled={!!reason}
                            onClick={() => setMode(id)}
                            data-testid={`delivery-note-mode-${id}`}
                            className={`text-left p-3 rounded-2xl border-2 transition ${mode === id ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-orange-300"} disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                            <Icon className="w-5 h-5 text-orange-500 mb-1" />
                            <div className="text-sm font-black text-slate-800">{label}</div>
                            <div className="text-[11px] text-slate-500">{reason || desc}</div>
                            {!reason && detail(id) && <div className="text-[10px] font-mono text-slate-400 truncate mt-1">{detail(id)}</div>}
                        </button>
                    );
                })}
            </div>
            {mode !== "onsite" && (
                <div className="text-[11px] text-slate-500 italic">Vous signez d'abord en tant que remettant, puis la demande part au salarié.</div>
            )}
        </div>
    );
}
